import { useAuth } from "react-oidc-context";
import { jwtDecode } from "jwt-decode";
import { useActiveOrg, GateHeader } from "common-fe";
import { Box, Typography, CircularProgress } from "@mui/material";
import { colors } from "brand";
import { useTranslation } from "i18n-fe";
import { manifest } from "score-common/permissions";
import { handleLogout } from "../utils/logout";

// AC#4 (#641) — the app requires login (ProtectedRoute, App.jsx) AND rejects a
// user without score_announcer (or an org-scoped score role) in the active
// org. "Org-scoped" = a role that carries list_shows_org (the whole show), not
// list_shows_mine (score_judge/score_viewer, scoped to shows that user is
// personally assigned to — not enough for an announcer, who needs the whole
// ring). Derived from the manifest rather than hardcoded so a future role that
// adds list_shows_org is picked up automatically.
//
// Mirrors scoreadmin-fe's ScoreEntitlementGate role check: the access token
// carries a `roles` claim, a per-org map `{ orgId: [roleNames] }` stamped by
// account's PreTokenGeneration trigger. Match qualified ("score:score_admin")
// or bare ("score_admin"). The API independently enforces this (score-api
// permission checks); this is the FE affordance.
const ORG_SCOPED_ROLES = Object.entries(manifest.roles)
  .filter(([, role]) => role.permissions.includes("list_shows_org"))
  .map(([key]) => key);

function activeOrgHasAnnouncerAccess(accessToken, activeOrgId) {
  if (!accessToken || !activeOrgId) return false;
  try {
    const claims = jwtDecode(accessToken);
    const raw = claims.roles;
    const map = typeof raw === "string" ? JSON.parse(raw) : raw || {};
    const roles = Array.isArray(map[activeOrgId]) ? map[activeOrgId] : [];
    return roles.some(
      (r) => ORG_SCOPED_ROLES.includes(r) || ORG_SCOPED_ROLES.includes(r?.replace(/^score:/, "")),
    );
  } catch {
    return false;
  }
}

export default function AnnouncerRoleGate({ children }) {
  const auth = useAuth();
  const { activeOrgId, isSuper, orgResolved } = useActiveOrg();
  const { t } = useTranslation("announcer");
  const accessToken = auth.user?.access_token;

  // #2120 (approved by Jaybill 2026-08-30, card comment): decide off
  // `orgResolved`, NOT the org-list network fetch (`loading`). For a
  // returning user, activeOrgId is already known synchronously from
  // localStorage (common-fe ActiveOrgContext), so this gate can check the
  // token's OWN roles claim for that org immediately, no waiting on the
  // GET /org round trip. Accepted trade: if the active org's role was
  // revoked since the token was minted, this can let a now-unauthorized
  // user in until they next get a fresh token — deliberately not mitigated
  // (see ActiveOrgContext.jsx). Only a brand-new session with no stored org
  // (activeOrgId still null) waits here, because there is genuinely no org
  // to check claims against yet.
  if (!orgResolved) {
    // #714 — primary.main is dark grey and invisible on a dark background;
    // force the brand accent so this gate's loading state stays visible.
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress sx={{ color: colors.foxOrange }} />
      </Box>
    );
  }

  if (isSuper || activeOrgHasAnnouncerAccess(accessToken, activeOrgId)) {
    return children;
  }

  // #881 — this gate sits OUTSIDE AnnouncerShell (#2112's persistent AppBar
  // with the org/show switcher), so a user whose active org lacks announcer
  // access never reaches that chrome and used to hit this message with zero
  // controls at all — no logout, no org switcher, only /logout typed by
  // hand. GateHeader adds just those two controls plus the active
  // identity/org, without pulling AnnouncerShell's app-bar in for a user who
  // isn't authorized to see the app it fronts.
  return (
    <>
      <GateHeader onLogout={handleLogout} />
      <Box sx={{ maxWidth: 560, mx: "auto", pt: "64px", pb: 8, px: 3, textAlign: "center" }}>
        <Box sx={{ pt: 8 }}>
          <Typography variant="h5" gutterBottom color="text.primary">
            {t("gate.heading")}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t("gate.body")}
          </Typography>
        </Box>
      </Box>
    </>
  );
}
